/**
 * This button component is built to December 2022 specifications from the design team.
 * https://www.figma.com/proto/Z6fObCiWnXEVBTtxCpKT4r/Button-specs?node-id=1%3A206&scaling=min-zoom&page-id=0%3A1&starting-point-node-id=1%3A206
 */

import classNames from 'classnames';
import React, {ReactElement} from 'react';

import {useBetaFeatureNightModeEnabled} from '../../util/useBetaFeature';
import {IconName} from '../Icon';
import {Tooltip} from '../Tooltip';
import * as S from './Button.styles';
import {ButtonIconSide, ButtonSize, ButtonVariant} from './types';

export type ButtonProps = {
  icon?: IconName;
  iconSide?: ButtonIconSide;
  size?: ButtonSize;
  variant?: ButtonVariant;
  children?: ReactElement | string;
  disabled?: boolean;
  fullWidth?: boolean;
  active?: boolean;

  tooltip?: string;

  // These are just passed through
  dataTest?: string;
  style?: Record<string, any>;
  className?: string;
  onClick?(e: React.MouseEvent): void;
};

export const Button = ({
  size,
  variant,
  icon,
  iconSide,
  disabled,
  fullWidth,
  active,
  children,
  dataTest,
  style,
  className,
  tooltip,
  onClick,
}: ButtonProps) => {
  let buttonIconSide: string = iconSide ?? 'left';
  if (!children && !icon) {
    console.error('Button: requires either text (children) or icon.');
  } else if (children && !icon) {
    buttonIconSide = 'textonly';
  } else if (!children && icon) {
    buttonIconSide = 'icononly';
  } else if (fullWidth) {
    console.error('Button: cannot use fullWidth with icon');
  }

  const isNightMode = useBetaFeatureNightModeEnabled();

  const buttonFullWidth = fullWidth ?? false;
  const buttonSize = size ?? 'medium';
  const buttonVariant = variant ?? 'primary';
  const loadedIcon = icon ? <S.Icon name={icon} size={buttonSize} /> : null;

  const text = children ? (
    <S.Text fullWidth={buttonFullWidth}>{children}</S.Text>
  ) : null;

  const body =
    buttonIconSide === 'right' ? (
      <>
        {text}
        {loadedIcon}
      </>
    ) : (
      <>
        {loadedIcon}
        {text}
      </>
    );

  const button = (
    <S.Button
      data-test={dataTest}
      className={classNames('night-aware', className)}
      isNightMode={isNightMode}
      variant={buttonVariant}
      size={buttonSize}
      iconSide={buttonIconSide}
      fullWidth={buttonFullWidth}
      active={!!active}
      style={style}
      disabled={!!disabled}
      onClick={onClick}>
      {body}
    </S.Button>
  );

  if (tooltip) {
    return <Tooltip content={tooltip} trigger={button} />;
  }
  return button;
};
